import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../firebase-config';
import '../../assets/styles/ClientShared.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserData = useCallback(async (user) => {
    const userDocRef = doc(db, 'users', user.uid);
    try {
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        setUserData(userData);
        fetchProjectData(userData.projectId);
      } else {
        setUserData(null);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setLoading(false);
    }
  }, []);

  const fetchProjectData = async (projectId) => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    const projectDocRef = doc(db, 'projects', projectId);
    try {
      const projectDocSnap = await getDoc(projectDocRef);
      if (projectDocSnap.exists()) {
        setProjectData(projectDocSnap.data());
      } else {
        setProjectData(null);
      }
    } catch (error) {
      console.error('Error fetching project data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/login');
      } else {
        fetchUserData(user);
      }
    });

    return () => unsubscribe();
  }, [navigate, fetchUserData]);

  const handleActionCompletion = (index) => {
    // Update the actions in the projectData state
    const updatedActions = [...projectData.actions];
    updatedActions[index].complete = !updatedActions[index].complete;

    // Update the projectData state with the modified actions
    setProjectData((prevData) => ({
      ...prevData,
      actions: updatedActions,
    }));

    // Persist the changes to Firestore if required
    updateActionsInFirestore(updatedActions);
  };

  const updateActionsInFirestore = async (updatedActions) => {
    try {
      const projectDocRef = doc(db, 'projects', userData.projectId);
      await updateDoc(projectDocRef, { actions: updatedActions });
    } catch (error) {
      console.error('Error updating actions:', error);
    }
  };

  const getPackageExperience = () => {
    const packageKey = (projectData.packageKey || projectData.packageType || '').toLowerCase();

    if (packageKey.includes('starter')) {
      return {
        label: 'Starter',
        tone: 'Fast and simple',
        description: 'This project is designed to move quickly. Complete the intake, upload the essentials, review once, then launch.',
      };
    }

    if (packageKey.includes('authority') || packageKey.includes('enterprise')) {
      return {
        label: projectData.packageDisplayName || 'Authority',
        tone: 'Custom workflow build',
        description: 'This project needs clear scope, workflow decisions, testing, and approval before launch.',
      };
    }

    if (packageKey.includes('custom')) {
      return {
        label: 'Custom',
        tone: 'Scoped custom build',
        description: 'This project is shaped around discovery, scope lock, and a custom implementation plan.',
      };
    }

    if (packageKey.includes('care')) {
      return {
        label: 'Care Plan',
        tone: 'Ongoing support',
        description: 'Your site is in support mode. Submit updates and keep track of what is being handled.',
      };
    }

    return {
      label: projectData.packageDisplayName || projectData.packageType || 'Growth',
      tone: 'Guided website build',
      description: 'This project is built around messaging, custom design, review rounds, and a clean launch.',
    };
  };

  const getNextAction = () => {
    const actions = projectData.actions || [];
    const nextAction = actions.find((action) => !action.complete && !action.locked);

    if (nextAction) {
      return nextAction;
    }

    const lockedAction = actions.find((action) => !action.complete);
    if (lockedAction) {
      return {
        ...lockedAction,
        locked: true,
        name: `Waiting on next stage: ${lockedAction.name}`,
      };
    }

    return {
      name: 'All client actions are complete',
      link: '',
      complete: true,
      locked: false,
    };
  };

  const handleNextActionClick = (action) => {
    if (!action.link || action.locked || action.complete) return;

    if (action.link.startsWith('http')) {
      window.open(action.link, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(action.link);
  };

  if (loading) {
    return (
      <div className="cp-loading">
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (!projectData) {
    return (
      <div className="cp-page">
        <div className="cp-page-header">
          <p className="cp-eyebrow">Client Portal</p>
          <h1 className="cp-title">Dashboard</h1>
        </div>
        <div className="cp-empty">
          <p>No project found. Please check back later or contact support if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  const status = (projectData.status || '').toLowerCase();
  const packageExperience = getPackageExperience();
  const nextAction = getNextAction();
  const currentStageData = projectData.stages?.[projectData.currentStage || 0];
  const currentStageName = typeof currentStageData === 'string'
    ? currentStageData
    : currentStageData?.name || 'Not started';
  const nextActionText = nextAction.complete
    ? 'Everything assigned to you is complete right now.'
    : nextAction.locked
      ? 'This action is locked until the project reaches the next stage.'
      : packageExperience.description;
  const projectFiles = projectData.projectFiles || {};
  const resources = projectData.resources || {};
  const carePlanStatus = projectData.carePlanStatus || 'not-offered';
  const carePlanIsActive = carePlanStatus === 'active';
  const carePlanWasOffered = carePlanStatus !== 'not-offered' || projectData.carePlanOffered;

  return (
    <div className="cp-page">
      {/* Page Header */}
      <div className="cp-page-header">
        <p className="cp-eyebrow">Client Portal</p>
        <h1 className="cp-title">{projectData.projectName}</h1>
      </div>

      <div className="cp-next-action">
        <div>
          <p className="cp-eyebrow">Next Action</p>
          <h2>{nextAction.name}</h2>
          <p>{nextActionText}</p>
          <div className="cp-next-meta">
            <span>{packageExperience.label}</span>
            <span>{packageExperience.tone}</span>
            <span>Current stage: {currentStageName}</span>
          </div>
        </div>
        <button
          className="cp-btn-primary"
          onClick={() => handleNextActionClick(nextAction)}
          disabled={!nextAction.link || nextAction.locked || nextAction.complete}
        >
          {nextAction.complete ? 'Complete' : nextAction.locked ? 'Locked' : 'Continue'}
        </button>
      </div>

      {carePlanWasOffered && (
        <div className="cp-section" style={{ marginBottom: '20px' }}>
          <div className="cp-section-header">
            <h2 className="cp-section-title">Care Plan</h2>
            <span className={`cp-badge ${carePlanIsActive ? 'cp-badge--active' : 'cp-badge--pending'}`}>
              {carePlanStatus}
            </span>
          </div>
          <div className="cp-section-body">
            <p style={{ fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', color: 'var(--text-secondary)', lineHeight: 1.7, margin: '0 0 16px' }}>
              {carePlanIsActive
                ? 'Your Care Plan is active. Submit small edits, support questions, or website issues here.'
                : 'Your Care Plan is not active yet. Once the subscription is confirmed, request access will unlock.'}
            </p>
            <button
              className="cp-btn-primary"
              onClick={() => navigate('/client/care-plan-request')}
              disabled={!carePlanIsActive}
            >
              {carePlanIsActive ? 'Manage Care Plan Requests' : 'Care Plan Locked'}
            </button>
          </div>
        </div>
      )}

      {/* 2-col grid: Project Overview + Tutorial Video */}
      <div className="cp-grid-2" style={{ marginBottom: '20px' }}>
        {/* Project Overview */}
        <div className="cp-section" style={{ margin: 0 }}>
          <div className="cp-section-header">
            <h2 className="cp-section-title">Project Overview</h2>
          </div>
          <div className="cp-section-body">
            <div className="cp-meta-row" style={{ flexDirection: 'column', gap: '12px' }}>
              <div className="cp-meta-item">
                <span className="cp-meta-label">Project</span>
                <span className="cp-meta-value">{projectData.projectName}</span>
              </div>
              <div className="cp-meta-item">
                <span className="cp-meta-label">Package</span>
                <span className="cp-meta-value">{projectData.packageDisplayName || projectData.packageType}</span>
              </div>
              <div className="cp-meta-item">
                <span className="cp-meta-label">Status</span>
                <span
                  className={`cp-badge ${
                    status === 'active'
                      ? 'cp-badge--active'
                      : status === 'complete'
                      ? 'cp-badge--done'
                      : 'cp-badge--pending'
                  }`}
                >
                  {projectData.status}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tutorial Video */}
        <div className="cp-section" style={{ margin: 0 }}>
          <div className="cp-section-header">
            <h2 className="cp-section-title">Tutorial Video</h2>
          </div>
          <div className="cp-section-body">
            {projectData.clientPortalTutorialVideo ? (
              <iframe
                width="100%"
                height="250"
                src={
                  projectData.clientPortalTutorialVideo.includes('loom.com/share')
                    ? projectData.clientPortalTutorialVideo.replace('loom.com/share', 'loom.com/embed')
                    : projectData.clientPortalTutorialVideo
                }
                title="Client Portal Tutorial"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            ) : (
              <p className="cp-empty-text">Tutorial video not yet available.</p>
            )}
          </div>
        </div>
      </div>

      {/* Current Stage Section */}
      <div className="cp-section" style={{ marginBottom: '20px' }}>
        <div className="cp-section-header">
          <h2 className="cp-section-title">Current Stage</h2>
        </div>
        <div className="cp-section-body" style={{ padding: 0 }}>
          <div className="cp-progress-wrap">
            <div className="cp-progress">
              {projectData.stages.map((stage, index) => {
                const done = projectData.currentStage > index;
                const current = projectData.currentStage === index;
                const stageName = typeof stage === 'string' ? stage : stage.name || 'Unnamed Stage';
                return (
                  <div
                    key={index}
                    className={`cp-progress-step${done ? ' done' : ''}${current ? ' current' : ''}`}
                  >
                    <div className="cp-progress-dot" />
                    <p className="cp-progress-label">{stageName}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Key Dates Section */}
      <div className="cp-section" style={{ marginBottom: '20px' }}>
        <div className="cp-section-header">
          <h2 className="cp-section-title">Key Dates</h2>
        </div>
        <div className="cp-section-body">
          {projectData.keyDates && projectData.keyDates.length > 0 ? (
            <ul className="cp-dates-list">
              {projectData.keyDates.map((d, index) => (
                <li key={index} className="cp-date-item">
                  <span className="cp-date-name">{d.name}</span>
                  <span className="cp-date-value">{d.date || 'Not Set'}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="cp-empty-text">No key dates set yet.</p>
          )}
        </div>
      </div>

      {/* Actions Section */}
      <div className="cp-section" style={{ marginBottom: '20px' }}>
        <div className="cp-section-header">
          <h2 className="cp-section-title">Project Actions</h2>
        </div>
          <div className="cp-action-grid">
          {(projectData.actions || []).map((action, i) => (
            <div
              key={i}
              className={`cp-action-card${action.locked ? ' locked' : ''}${action.complete ? ' completed' : ''}`}
            >
              {action.locked && <span className="cp-lock-badge">Locked</span>}
              <p className="cp-action-name">{action.name}</p>
              <a
                href={action.link}
                className="cp-action-link"
                onClick={(e) => {
                  if (action.locked) e.preventDefault();
                }}
              >
                Go to Action &rarr;
              </a>
              <div className="cp-action-complete">
                <input
                  type="checkbox"
                  id={`a-${i}`}
                  checked={action.complete}
                  disabled={action.locked}
                  onChange={() => handleActionCompletion(i)}
                />
                <label htmlFor={`a-${i}`}>
                  {action.complete ? 'Completed' : 'Mark as complete'}
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Deliverables Section */}
      <div className="cp-section" style={{ marginBottom: '20px' }}>
        <div className="cp-section-header">
          <h2 className="cp-section-title">Deliverables</h2>
        </div>
        <div className="cp-section-body">
          <ul className="cp-list">
            {(projectData.deliverables || []).map((item, index) => (
              <li key={index} className="cp-list-item">{item}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Documents Section */}
      <div className="cp-section" style={{ marginBottom: '20px' }}>
        <div className="cp-section-header">
          <h2 className="cp-section-title">Documents</h2>
        </div>
        <div className="cp-section-body">
          {projectData?.proposalDocuments ? (
            <div className="cp-doc-list">
              {projectData.proposalDocuments.proposal && (
                <div className="cp-doc-item">
                  <span className="cp-doc-label">Proposal</span>
                  <button
                    className="cp-doc-link"
                    onClick={() => navigate(`/proposal/${userData.projectId}`)}
                  >
                    View &rarr;
                  </button>
                </div>
              )}
              {projectData.proposalDocuments.contract && (
                <div className="cp-doc-item">
                  <span className="cp-doc-label">Contract</span>
                  <a
                    className="cp-doc-link"
                    href={
                      projectData.proposalDocuments.contract.startsWith('http')
                        ? projectData.proposalDocuments.contract
                        : `https://${projectData.proposalDocuments.contract}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View &rarr;
                  </a>
                </div>
              )}
              {projectData.proposalDocuments.invoice && (
                <div className="cp-doc-item">
                  <span className="cp-doc-label">Invoice</span>
                  <a
                    className="cp-doc-link"
                    href={
                      projectData.proposalDocuments.invoice.startsWith('http')
                        ? projectData.proposalDocuments.invoice
                        : `https://${projectData.proposalDocuments.invoice}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View &rarr;
                  </a>
                </div>
              )}
            </div>
          ) : (
            <p className="cp-empty-text">No documents uploaded yet.</p>
          )}
        </div>
      </div>

      {/* Project Files Section */}
      <div className="cp-section" style={{ marginBottom: '20px' }}>
        <div className="cp-section-header">
          <h2 className="cp-section-title">Project Files</h2>
        </div>
        <div className="cp-section-body">
          <div className="cp-doc-list">
            {Object.entries(projectFiles).map(([key, value]) => (
              <div key={key} className="cp-doc-item">
                <span className="cp-doc-label">
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </span>
                {value ? (
                  <a
                    className="cp-doc-link"
                    href={value.startsWith('http') ? value : `https://${value}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View &rarr;
                  </a>
                ) : (
                  <span className="cp-doc-missing">Not uploaded</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Resources Section */}
      <div className="cp-section" style={{ marginBottom: '24px' }}>
        <div className="cp-section-header">
          <h2 className="cp-section-title">Resources</h2>
        </div>
        <div className="cp-section-body">
          <div className="cp-doc-list">
            {Object.entries(resources).map(([key, value]) => (
              <div key={key} className="cp-doc-item">
                <span className="cp-doc-label">
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </span>
                {key.toLowerCase() === 'trainingvideo' && value && value.includes('loom.com/share') ? (
                  <iframe
                    width="100%"
                    height="250"
                    src={value.replace('loom.com/share', 'loom.com/embed')}
                    title="Training Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                ) : (
                  <button className="cp-doc-link" onClick={() => navigate(value)}>
                    View &rarr;
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
